import { useEffect, useMemo, useRef, useState } from "react";
import type { Prospect } from "./ProspectCard";
import { copyText } from "./OpenAndWritePanel";
import {
  buildTrackingLink,
  fetchMessages,
  logOutgoing,
  logReply,
  type ProspectMessage,
  type ProspectPatch,
} from "@/services/conversations";
import { INTENT_LABELS, INTENT_ORDER, buildSuggestion, detectIntent, type ReplyIntent } from "@/data/replySuggestions";
import { timeAgo } from "@/lib/timeAgo";
import { friendlyFirstName } from "@/data/outreach";
import { askAiReply, type AiDraft } from "@/services/aiReply";

type AiState = { status: "idle" | "loading" | "ok" | "fallback" | "max"; draft?: AiDraft };

type Props = {
  prospect: Prospect;
  onClose: () => void;
  /** Refleja en la lista los cambios (etapa, próxima acción, contador de respuestas…). */
  onPatch: (id: string, patch: ProspectPatch) => void;
  onToast: (msg: string) => void;
};

export default function ConversationPanel({ prospect, onClose, onPatch, onToast }: Props) {
  const [messages, setMessages] = useState<ProspectMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [intent, setIntent] = useState<ReplyIntent>("other");
  const [intentTouched, setIntentTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState("");
  const [reply, setReply] = useState("");
  const [ai, setAi] = useState<AiState>({ status: "idle" });
  const [followupMode, setFollowupMode] = useState(false);
  const aiForMsg = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const counts = useRef({ replies: prospect.replyCount ?? 0, stage: prospect.status });

  const firstName = friendlyFirstName(prospect.name);
  const channel = prospect.platform.toLowerCase();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchMessages(prospect.id)
      .then((m) => alive && setMessages(m))
      .catch((e) => alive && setError(e instanceof Error ? e.message : "No se pudo cargar la conversación."))
      .finally(() => alive && setLoading(false));
    buildTrackingLink(prospect.trackCode, prospect.platform)
      .then((l) => alive && setLink(l))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [prospect.id, prospect.trackCode, prospect.platform]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, loading]);

  // Detecta la intención mientras pegas el texto (puedes corregirla con los chips).
  useEffect(() => {
    if (!intentTouched) setIntent(detectIntent(draft));
  }, [draft, intentTouched]);

  const last = messages[messages.length - 1];
  const pendingIn = last && last.direction === "in" ? last : null;

  const suggestion = useMemo(() => {
    if (!pendingIn) return null;
    const i = (INTENT_ORDER as string[]).includes(pendingIn.intent ?? "") ? (pendingIn.intent as ReplyIntent) : detectIntent(pendingIn.body);
    return buildSuggestion(i, firstName, link || "(link único)");
  }, [pendingIn, firstName, link]);

  // Al llegar una respuesta nueva (o cargar el link), reinicia el texto sugerido.
  useEffect(() => {
    if (suggestion) setReply(suggestion.text);
  }, [suggestion?.text]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAi = async (mode: "reply" | "followup") => {
    if (!link) return;
    setAi({ status: "loading" });
    const res = await askAiReply({ prospectId: prospect.id, mode, firstName, link });
    if (res.ok) {
      setReply(res.draft.text);
      setAi({ status: "ok", draft: res.draft });
    } else if ((res as { reason?: string }).reason === "max_touches") {
      setAi({ status: "max" });
    } else {
      if (mode === "followup") setReply(`¡Hola${firstName ? ` ${firstName}` : ""}! Paso a saludarte por si no alcanzaste a ver mi mensaje 🙂 Si te interesa, aquí te dejo el enlace del webinar gratuito: ${link}`);
      setAi({ status: "fallback" });
    }
  };

  // Al llegar una respuesta nueva, la IA redacta la contestación (si falla queda la sugerencia básica).
  useEffect(() => {
    if (!pendingIn || !link || aiForMsg.current === pendingIn.id) return;
    aiForMsg.current = pendingIn.id;
    setFollowupMode(false);
    void runAi("reply");
  }, [pendingIn?.id, link]); // eslint-disable-line react-hooks/exhaustive-deps

  const startFollowup = () => {
    setFollowupMode(true);
    void runAi("followup");
  };
  const registerReply = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const sug = buildSuggestion(intent, firstName, link);
      const { message, patch } = await logReply({
        prospectId: prospect.id,
        body,
        intent,
        channel,
        currentStage: counts.current.stage,
        currentReplyCount: counts.current.replies,
        suggestedStage: sug.stage,
        nextAction: sug.nextAction,
      });
      counts.current = { replies: patch.replyCount ?? counts.current.replies, stage: patch.status ?? counts.current.stage };
      setMessages((m) => [...m, message]);
      onPatch(prospect.id, patch);
      setDraft("");
      setIntentTouched(false);
      onToast("Respuesta registrada");
    } catch (e) {
      onToast(e instanceof Error ? `No se pudo guardar: ${e.message}` : "No se pudo guardar la respuesta.");
    } finally {
      setSaving(false);
    }
  };

  const markSent = async () => {
    const body = reply.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      const { message, patch } = await logOutgoing({
        prospectId: prospect.id,
        body,
        channel,
        waitingAction: ai.status === "ok" ? ai.draft?.nextAction || undefined : undefined,
        followupDays: ai.status === "ok" ? ai.draft?.followupDays : undefined,
      });
      setMessages((m) => [...m, message]);
      onPatch(prospect.id, patch);
      setFollowupMode(false);
      setAi({ status: "idle" });
      onToast("Tu respuesta quedó registrada");
    } catch (e) {
      onToast(e instanceof Error ? `No se pudo guardar: ${e.message}` : "No se pudo guardar tu respuesta.");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text: string, okMsg: string) => {
    const ok = await copyText(text);
    onToast(ok ? okMsg : "No se pudo copiar. Selecciona el texto y cópialo.");
  };

  const linkStatus = prospect.linkRegistered
    ? { icon: "ri-user-add-line", text: "Se registró en el webinar", tone: "text-primary-700" }
    : prospect.linkClicks
      ? {
          icon: "ri-cursor-line",
          text: `Abrió el link${prospect.linkClicks > 1 ? ` (${prospect.linkClicks} veces)` : ""}${prospect.linkLastClickAt ? ` · ${timeAgo(prospect.linkLastClickAt)}` : ""}`,
          tone: "text-accent-700",
        }
      : { icon: "ri-time-line", text: "Aún no ha abierto el link", tone: "text-foreground-500" };
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/50" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="bg-background-50 w-full md:max-w-xl max-h-[92vh] md:max-h-[88vh] rounded-t-3xl md:rounded-3xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-4 border-b border-background-200">
          <img src={prospect.photo} alt="" className="w-11 h-11 rounded-xl object-cover object-top flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-bold text-foreground-950 truncate">{prospect.name}</h3>
            <p className="text-xs text-foreground-500 truncate">
              {prospect.platform} · {prospect.username}
            </p>
            <p className={`text-xs font-semibold mt-1 inline-flex items-center gap-1 ${linkStatus.tone}`}>
              <i className={linkStatus.icon}></i> {linkStatus.text}
            </p>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="w-9 h-9 rounded-full hover:bg-background-200 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-xl px-3 py-2">
            <i className="ri-link text-foreground-400"></i>
            <span className="text-xs text-foreground-600 truncate flex-1">{link || "Preparando link…"}</span>
            <button
              onClick={() => link && void copy(link, "Link único copiado")}
              disabled={!link}
              className="text-xs font-semibold text-primary-700 hover:text-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Copiar link
            </button>
          </div>
          <p className="text-[11px] text-foreground-500 mt-1">Este link es solo de este prospecto: así sabes si lo abrió o se registró.</p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[120px]">
          {loading ? (
            <p className="text-center text-sm text-foreground-500 py-6">
              <i className="ri-loader-4-line animate-spin"></i> Cargando…
            </p>
          ) : error ? (
            <p className="text-center text-sm text-accent-700 py-6">{error}</p>
          ) : messages.length === 0 ? (
            <p className="text-center text-sm text-foreground-500 py-6">
              Aún no hay mensajes. Cuando {firstName || "la persona"} te responda en {prospect.platform}, pega aquí lo que escribió.
            </p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line ${
                    m.direction === "out" ? "bg-primary-500 text-background-950 rounded-br-md" : "bg-background-200 text-foreground-900 rounded-bl-md"
                  }`}
                >
                  {m.body}
                  <div className="text-[10px] opacity-60 mt-1 flex items-center gap-1.5">
                    <span>{m.direction === "out" ? "Tú" : firstName || "Ella"}</span>
                    {m.intent && m.direction === "in" ? <span>· {INTENT_LABELS[m.intent as ReplyIntent] ?? m.intent}</span> : null}
                    <span>· {timeAgo(m.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        {!pendingIn && !followupMode && ai.status !== "max" && (messages.length > 0 || prospect.status !== "Nuevo") && !loading ? (
          <button
            onClick={startFollowup}
            disabled={!link}
            className="w-full py-2.5 rounded-xl border border-primary-500/40 text-primary-700 bg-primary-500/5 font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-500/15 disabled:opacity-50"
          >
            <i className="ri-magic-line"></i> Redactar seguimiento con IA
          </button>
        ) : null}
        {ai.status === "max" && !pendingIn ? (
          <p className="text-xs text-foreground-600 bg-background-100 border border-background-200 rounded-xl p-3">
            Ya hiciste 3 seguimientos sin respuesta. Lo mejor es no insistir más: puedes dejarlo en pausa o descartarlo.
          </p>
        ) : null}
        {suggestion || followupMode ? (
          <div className="rounded-2xl border border-accent-500/30 bg-accent-500/5 p-3">
            <p className="text-xs font-semibold text-accent-700 inline-flex items-center gap-1.5 mb-2">
              <i className="ri-magic-line"></i>{" "}
              {suggestion
                ? `Cómo responderle · ${INTENT_LABELS[(ai.status === "ok" && ai.draft ? ai.draft.intent : suggestion.intent) as ReplyIntent] ?? INTENT_LABELS[suggestion.intent]}`
                : `Seguimiento${ai.draft?.touch ? ` ${ai.draft.touch} de ${ai.draft.maxTouches}` : ""}`}
            </p>
            <p className="text-[11px] text-foreground-600 mb-1.5 flex items-center gap-2">
              {ai.status === "loading" ? (
                <span><i className="ri-loader-4-line animate-spin"></i> La IA está redactando…</span>
              ) : ai.status === "ok" ? (
                <span><i className="ri-sparkling-line text-accent-700"></i> Redactado por la IA de Vívelo. Revísalo antes de enviar.</span>
              ) : ai.status === "fallback" ? (
                <span>La IA no respondió; te dejo una sugerencia básica.</span>
              ) : null}
              {ai.status !== "loading" && link ? (
                <button onClick={() => void runAi(suggestion ? "reply" : "followup")} className="ml-auto font-semibold text-primary-700 cursor-pointer whitespace-nowrap">
                  <i className="ri-refresh-line"></i> Otra versión
                </button>
              ) : null}
            </p>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={5}
              className="w-full text-sm bg-background-50 border border-background-200 rounded-xl p-2.5 outline-none focus:border-primary-400 resize-y"
            />
            <div className="grid grid-cols-3 gap-2 mt-2">
              <button
                onClick={() => void copy(reply, "Respuesta copiada")}
                className="py-2 rounded-xl border border-background-300 text-xs font-semibold cursor-pointer hover:border-primary-400 whitespace-nowrap"
              >
                <i className="ri-file-copy-line"></i> Copiar
              </button>
              <button
                onClick={() => prospect.profileUrl && window.open(prospect.profileUrl, "_blank", "noopener,noreferrer")}
                disabled={!prospect.profileUrl}
                className="py-2 rounded-xl border border-background-300 text-xs font-semibold cursor-pointer hover:border-primary-400 whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-external-link-line"></i> Abrir perfil
              </button>
              <button
                onClick={() => void markSent()}
                disabled={saving}
                className="py-2 rounded-xl bg-secondary-500 text-white text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-send-plane-line"></i> Ya la envié
              </button>
            </div>
            <p className="text-[10px] text-foreground-500 mt-2">Revísala y ajústala. Vívelo no envía nada: tú la pegas y la envías desde tu cuenta.</p>
          </div>
        ) : null}
          <div ref={endRef} />
        </div>

        <div className="p-4 border-t border-background-200 bg-background-100">
          <label className="text-xs font-semibold text-foreground-700">
            {pendingIn ? "¿Escribió algo más?" : `Pega lo que ${firstName || "ella"} te respondió`}
          </label>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Ej: Hola, ¿de qué se trata?"
            className="mt-1 w-full text-sm bg-background-50 border border-background-200 rounded-xl p-2.5 outline-none focus:border-primary-400 resize-none"
          />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {INTENT_ORDER.map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setIntent(i);
                  setIntentTouched(true);
                }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer whitespace-nowrap border ${
                  intent === i ? "bg-primary-500 text-background-950 border-primary-500" : "bg-background-50 text-foreground-600 border-background-300 hover:border-primary-400"
                }`}
              >
                {INTENT_LABELS[i]}
              </button>
            ))}
          </div>
          <button
            onClick={() => void registerReply()}
            disabled={!draft.trim() || saving}
            className="mt-3 w-full py-3 rounded-xl bg-primary-500 text-background-950 font-heading font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-chat-check-line"></i>} Registrar respuesta
          </button>
        </div>
      </div>
    </div>
  );
}